// Host site / user hint types — mirrors PROTOCOL.md §1.

export interface SiteContext {
  siteId: string;
  pageUrl: string;
  pageTitle?: string;
  pageKind?: string;
  custom?: Record<string, unknown>;
}

/**
 * UserHint is a HINT only — NOT auth. See PROTOCOL.md §Security.
 * The widget never makes auth decisions from this.
 */
export interface UserHint {
  id?: string;
  role?: 'b2c' | 'b2b' | 'corporate' | 'agent';
  authToken?: string;
  /** Host-supplied phone hint (unverified). Used as contact fallback when the JWT carries no phone_number claim. */
  phone?: string;
}

export type Locale = 'vi' | 'en';

/**
 * Wire-level locale on `POST /api/v1/chat` and `POST /api/v1/chat/stream`
 * (and the bridge `init` envelope). `'auto'` tells the BE to auto-detect
 * the language from the latest user message (FEATURE-42 / FEATURE-56).
 * Omitting `locale` from the request body has the same observable BE
 * behavior — see PROTOCOL.md §1. FEATURE-66.
 *
 * Stays separate from `Locale` because UI-side consumers (translator,
 * speech recognizer) need a concrete two-character code; widening the
 * UI `Locale` to admit `'auto'` would force every component to handle
 * a code that has no BCP-47 equivalent.
 */
export type RequestLocale = Locale | 'auto';

/**
 * Theme the widget renders in. Selected by the host site at init time;
 * may change at runtime via `BAAssistant.updateTheme(...)`.
 *
 * Unknown values fall back silently to `'default'` with a console.warn —
 * the widget MUST keep working for end users regardless of host config.
 *
 * Added in FEATURE-34 (additive within protocol v1).
 */
export type ThemeName = 'default' | 'worldtrans';

/**
 * Compile-time known-themes list. Validators on both the launcher and the
 * panel side use this to resolve an unknown name to `'default'`.
 */
export const THEME_NAMES: readonly ThemeName[] = ['default', 'worldtrans'] as const;
